import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { usePushdownContext } from '../Pushdown/PushdownProvider';
import SessionStorage from '../../utils/SessionStorage';
import { InitializeAcquisitionApiResponse } from '../../types/responses/InitializeAcquisitionApi';
import DefaultSelfieFeedback from './MitekFeedback/SelfieFeedback';
import DefaultAutoCaptureHints from './MitekFeedback/AutoCaptureFeedback';
import { TFeedbackMap } from './MitekFeedback/FeedbackKeys';
import DefaultGeneralHints from './MitekFeedback/GeneralHintFeedback';
import useMitekErrorHandling from './MitekErrorHandling';
import { formatBase64ImageDataForSubmission, getApplicantId } from './MitekUtils';
import { getApiToken } from '../../utils/cob-utils';

const DocumentTypes = ['DL_FRONT', 'PDF417_BARCODE', 'PASSPORT', 'SELFIE'] as const;
export type SupportedDocumentTypes = (typeof DocumentTypes)[number] | 'UNKNOWN';

export interface IMitekCaptureConfiguration {
    mode: 'AUTO_CAPTURE' | 'MANUAL_CAPTURE';
    documentType: SupportedDocumentTypes;
    mitekSDKPath: string;
    options?: {
        qualityPercent?: number;
        guidePaddingLevel: number;
        license: string;
    };
}

export interface IMitekCaptureResult {
    documentType: SupportedDocumentTypes;
    successful: boolean;
    errorMessage: string | undefined;
}

export type TMitekImage = string;
// PDF415 uses an additional code field
export type TMitekCaptureBundle = { imageData: TMitekImage; code?: string; status?: string; mode?: string };
export type TMitekCaptureTypes = TMitekImage | TMitekCaptureBundle;

export type TMitekResults = {
    [key in SupportedDocumentTypes]?: TMitekImage | TMitekCaptureBundle;
};

export type TMitekDocumentSubmission = {
    SessionId: string;
    IdentificationDocuments: TMitekImage[];
    BiometricFaceImage: string,
    IdentificationBarCode: string;
};

export type TMitekDocumentModernSubmission = {
    identityDocuments: {
        documentNumber: string,
        encodedData: {
            encodedData: string,
            encoding: string,
        }
    }[];
    images: {
        id: string,
        fileType: string,
        imageData: string,
    }[];
}

export interface IMitekContext {
    MitekResults: React.MutableRefObject<{
        [key in SupportedDocumentTypes]?:
        | TMitekImage
        | { imageData: TMitekImage; code?: string };
    }>;
    MitekProps: IMitekProps;
    MitekConfiguration: React.MutableRefObject<IMitekCallConfig>;
    initMitek: () => Promise<boolean>;
    startCapture: (callback: (result: IMitekCaptureResult) => any) => Promise<void>;
    retrieveResultsToSubmit: () => TMitekDocumentSubmission;
    retrieveResultsToSubmitModern: () => TMitekDocumentModernSubmission;
    clearResults: () => void;
    setMitekProps: React.Dispatch<React.SetStateAction<IMitekProps>>;
}

export const MitekContext = createContext({} as IMitekContext);
export const useMitekContext = () => useContext(MitekContext);

export interface IMitekProps {
    usingModernApi?: boolean,
    mitekDocumentType: SupportedDocumentTypes;
    mitekQualityLevel: number;
    mitekGuidePaddingLevel: number;
    mitekRetryAmount: number;
    mitekSessionTimeout: number;
    usingMitekManualFallback: boolean;
    usingUpdateDesktop: boolean;
    mitekSdkPath?: string;
    errorApiPushdownCode: string;
    errorSelfPushdownCode: string;
    errorGeneralPushdownCode: string;
    confirmationPageUrl: string;
    riskBasedEsignUrl: string;
    pendingApprovalPageUrl: string;
    instantIssuancePageUrl: string;
    mobileWalletPageUrl: string;
    levelThreeQuestionsPageUrl: string;
    oneTimePasscodePageUrl: string;
    limitedEsignPageUrl: string;
    pendingFundingUrl: string;
    dataEntryPageUrl: string;
    mitekSuccessRedirectPageUrl: string;
    mitekRetakePageUrl: string;
    mitekFileUploadPageUrl: string;
    mitekMobileDocumentSubmissionSuccessRedirectPageUrl: string;
}

interface IMitekCallConfig {
    SessionId: string;
    Token: string;
    isMobileOnlySession: boolean;
    licenseKey: string;
    captureMode: 'AUTO_CAPTURE' | 'MANUAL_CAPTURE';
}

export const MitekContextProvider = ({ children }: PropsWithChildren<{}>): JSX.Element => {
    const [MitekProps, setMitekProps] = useState<IMitekProps>({
        mitekDocumentType: 'DL_FRONT',
        mitekQualityLevel: 80,
        mitekGuidePaddingLevel: 1,
        mitekRetryAmount: 1,
        mitekSessionTimeout: 300,
        usingMitekManualFallback: true,
        usingUpdateDesktop: true,
        errorApiPushdownCode: 'apiErrorCode',
        errorSelfPushdownCode: 'apiErrorCode',
        errorGeneralPushdownCode: 'apiErrorCode',
        confirmationPageUrl: '',
        riskBasedEsignUrl: '',
        pendingApprovalPageUrl: '',
        instantIssuancePageUrl: '',
        mobileWalletPageUrl: '',
        levelThreeQuestionsPageUrl: '',
        oneTimePasscodePageUrl: '',
        limitedEsignPageUrl: '',
        dataEntryPageUrl: '',
        mitekSuccessRedirectPageUrl: '',
        mitekRetakePageUrl: '',
        mitekFileUploadPageUrl: '',
        mitekMobileDocumentSubmissionSuccessRedirectPageUrl: '',
        pendingFundingUrl: '',
    });
    const MitekConfigurationKey = 'mitekConfig' as const;
    const MitekConfiguration = useRef<IMitekCallConfig>({
        SessionId: '',
        Token: '',
        isMobileOnlySession: false,
        licenseKey: '',
        captureMode: 'AUTO_CAPTURE',
    });
    const MitekResultsKey = 'mitekResults' as const;
    const MitekResults = useRef<TMitekResults>({});
    const MitekSessionTimeoutHandle = useRef(null);
    const [mitekScienceSDK, setMitekScienceSdk] = useState(null);

    const { ErrorHandlingHookGenerator } = useMitekErrorHandling();

    const pushdownContext = usePushdownContext();

    const getMitekToken = (): string => {
        const token = getApiToken('mitekKey') as string;
        return token;
    };

    const getMitekConfig = () => {
        const currentConfig = SessionStorage.tryGetObject(MitekConfigurationKey, MitekConfiguration.current);

        // Theoretically, Only possible in Desktop + Mobile Session
        const queryParams = new URLSearchParams(window.location.search);

        const mitekConfig = {
            ...currentConfig,
            captureMode: MitekConfiguration.current?.captureMode ?? 'AUTO_CAPTURE',
            SessionId: queryParams.get('sid') ?? currentConfig?.SessionId,
            Token: queryParams.get('tkn') ?? currentConfig?.Token,
            isMobileOnlySession: currentConfig?.isMobileOnlySession || !queryParams.get('sid'),
            licenseKey: currentConfig?.licenseKey || getMitekToken(),
        };


        SessionStorage.setObject(MitekConfigurationKey, mitekConfig);
        MitekConfiguration.current = mitekConfig;
    };

    useEffect(() => {
        if (!window.location.href.includes('ID-verify')) {
            return;
        }
        // mitekScienceSDK is currently exported via mitek-science-sdk.js
        // window.mitekScienceSDK = initialised Mitek SDK

        // Acquisitions API Persistence + Mitek Configuration Persistence
        getMitekConfig();

        // Mitek Results Persistence
        if (SessionStorage.verifyNotEmpty(MitekResultsKey)) {
            MitekResults.current = SessionStorage.getObject(MitekResultsKey)!;
        }

        window.addEventListener('load', () => {
            setMitekScienceSdk((window as any).mitekScienceSDK);
        });

        window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                if ((window as any).mitekScienceSDK) {
                    (window as any).mitekScienceSDK.cmd('SDK_STOP');
                }
            }
        });
    }, []);

    useEffect(() => {
        if (MitekConfiguration.current.licenseKey) {
            (window as any).mitekScienceSDK.cmd('COMPONENT_PRELOAD', {
                mitekSDKPath: '/etc.clientlibs/cob-corp-acquisition/clientlibs/clientlib-mitek/resources',
                preloadComponents: ['DOCUMENTS'],
                options: {
                    license: MitekConfiguration.current.licenseKey,
                },
            });
        }
    }, [MitekConfiguration.current.licenseKey]);

    const validateMitekConfiguration = () => {
        if (!DocumentTypes.includes(MitekProps.mitekDocumentType as any)) {
            throw 'Unsupported Document Type';
        }
        if (!(window as any).mitekScienceSDK) {
            if (mitekScienceSDK === null) {
                throw 'Unable to find Mitek SDK';
            } else {
                (window as any).mitekScienceSDK = mitekScienceSDK;
            }
        }
    };

    const initMitek = async (): Promise<boolean> => {
        const params = new URLSearchParams(window.location.search);
        getMitekConfig();

        if (!MitekConfiguration.current.SessionId || !MitekConfiguration.current.Token) {
            const existingSession: InitializeAcquisitionApiResponse = SessionStorage.tryGetObject('initResponse', {});
            const existingApplication = await getApplicantId();

            // Developer note: Session ID is used as the name to obfuscate that this is the Applicant ID. Discuss any changes of this with Backend API team.
            if (!MitekConfiguration.current.SessionId && existingApplication) {
                MitekConfiguration.current.SessionId = existingApplication;
            }
            if (!MitekConfiguration.current.Token && existingSession.Token) {
                MitekConfiguration.current.Token = existingSession.Token;
            }
        }
        MitekConfiguration.current.isMobileOnlySession = MitekConfiguration.current.isMobileOnlySession || !params.get('sid');

        try {
            validateMitekConfiguration();
        } catch (errors) {
            console.error('Unable to initialize Mitek API!');
            pushdownContext.showPushdown(MitekProps.errorSelfPushdownCode);
            return false;
        }
        (window as any).mitekScienceSDK.on('SDK_ERROR', (error: any) => {
            console.error(error);
        });

        return true;
    };

    const retrieveResultsToSubmit = (): TMitekDocumentSubmission => {
        const results = MitekResults.current;
        const identificationDocuments: TMitekImage[] = [];

        if (
            results.PDF417_BARCODE
            && typeof results.PDF417_BARCODE !== 'string'
        ) {
            identificationDocuments.push(
                formatBase64ImageDataForSubmission(results.PDF417_BARCODE.imageData),
            );
        }
        if (results.DL_FRONT
            && typeof results.DL_FRONT === 'string'
        ) {
            identificationDocuments.push(
                formatBase64ImageDataForSubmission(results.DL_FRONT),
            );
        }
        if (results.PASSPORT
            && typeof results.PASSPORT === 'string'
        ) {
            identificationDocuments.push(
                formatBase64ImageDataForSubmission(results.PASSPORT),
            );
        }
        return {
            BiometricFaceImage: (formatBase64ImageDataForSubmission(results?.SELFIE as TMitekImage) as string) ?? '',
            IdentificationDocuments: identificationDocuments,
            IdentificationBarCode: (results?.PDF417_BARCODE as TMitekCaptureBundle)?.code ?? '',
            SessionId: MitekConfiguration.current.SessionId,
        };
    };

    const retrieveResultsToSubmitModern = (): TMitekDocumentModernSubmission => {
        let counter = 1;

        const results = MitekResults.current;

        const identityDocumentsToSubmit = [];
        const imagesToSubmit = [];

        identityDocumentsToSubmit.push({
            "documentNumber": "1",
            "encodedData": {
                "encodedData": "string",
                "encoding": "PDF_417",
            }
        })

        imagesToSubmit.push({
            "id": "IMAGE_1",
            "fileType": "JPEG",
            "imageData": "string",
        })

        if (
            results.PDF417_BARCODE
            && typeof results.PDF417_BARCODE !== 'string'
        ) {
            imagesToSubmit.push({ 
                    "imageData": formatBase64ImageDataForSubmission(results.PDF417_BARCODE.imageData),
                    "fileType": "JPEG",
                    "id": "IMAGE_" + counter
                }
            );
            identityDocumentsToSubmit.push({
                "documentNumber": "" + counter,
                "encodedData": {
                    "encodedData": results.PDF417_BARCODE.code,
                    "encoding": "PDF_417",
                }
            })
            counter += 1;
        }

        if (results.DL_FRONT
            && typeof results.DL_FRONT === 'string'
        ) {
            imagesToSubmit.push({
                "imageData": formatBase64ImageDataForSubmission(results.DL_FRONT),
                "fileType": "JPEG",
                "id": "IMAGE_" + counter,
            });
            counter += 1;
        }

        if (results.PASSPORT
            && typeof results.PASSPORT === 'string'
        ) {
            imagesToSubmit.push({
                "imageData": formatBase64ImageDataForSubmission(results.PASSPORT),
                "fileType": "JPEG",
                "id": "IMAGE_" + counter,
            });
        }
        
        return {
            identityDocuments: identityDocumentsToSubmit,
            images: imagesToSubmit,
        };
    };

    const MitekHandleCaptureCallback =
        (callback: (results: IMitekCaptureResult) => any) =>
            (result: { response: TMitekCaptureBundle }) => {
                if (MitekSessionTimeoutHandle.current !== null) {
                    clearTimeout(MitekSessionTimeoutHandle.current);
                }

                if (
                    MitekProps.usingMitekManualFallback
                    && result.response?.mode === 'AUTO'
                    && result.response?.status === 'failure'
                ) {
                    MitekConfiguration.current.captureMode = 'MANUAL_CAPTURE';
                    pushdownContext.showPushdown(MitekProps.errorSelfPushdownCode);
                    return;
                }

                const captureResponse = {
                    documentType: MitekProps.mitekDocumentType,
                    errorMessage: '',
                    successful: false,
                };
                if (!result?.response?.imageData && result?.response?.imageData === '') {
                    captureResponse.errorMessage = 'Failed to Capture';
                    callback(captureResponse);
                    pushdownContext.showPushdown(MitekProps.errorSelfPushdownCode);
                    return;
                }

                if (MitekProps.mitekDocumentType === 'PDF417_BARCODE') {
                    if (!result?.response?.code) {
                        console.error('MitekFailure: Expected barcode data');
                        captureResponse.errorMessage = 'Failed to Capture';
                        callback(captureResponse);
                        pushdownContext.showPushdown(MitekProps.errorSelfPushdownCode);
                        return;
                    }
                    MitekResults.current.PDF417_BARCODE = { ...result.response };
                } else {
                    MitekResults.current[MitekProps.mitekDocumentType] = result.response.imageData;
                }
                SessionStorage.setObject(MitekResultsKey, MitekResults.current);

                captureResponse.successful = true;
                callback(captureResponse);
            };

    const showHint = (hintToShow: string | Function | undefined) => {
        if (hintToShow) {
            (window as any).mitekScienceSDK.cmd(
                'SHOW_HINT',
                hintToShow,
            );
        }
    };

    const MitekHandleFeedbackCallback =
        (feedback: { key: keyof TFeedbackMap }) => {
            if (MitekConfiguration.current.captureMode === 'AUTO_CAPTURE') {
                if (MitekProps.mitekDocumentType === 'SELFIE' && feedback.key in DefaultSelfieFeedback) {
                    showHint(DefaultSelfieFeedback[feedback.key]);
                    return;
                }
                if (feedback.key in DefaultAutoCaptureHints) {
                    showHint(DefaultAutoCaptureHints[feedback.key]);
                    return;
                }
            }

            if (feedback.key in DefaultGeneralHints) {
                showHint(DefaultGeneralHints[feedback.key]);
                return;
            }
        };

    const MitekCapture = async (callback: (results: IMitekCaptureResult) => any) => {
        try {
            if (!await initMitek()) {
                throw 'Unable to initialise Mitek';
            }
            validateMitekConfiguration();
        } catch (e) {
            console.error('Unable to begin Mitek Capture!');
            pushdownContext.showPushdown(MitekProps.errorSelfPushdownCode);
            return;
        }

        const captureConfiguration: IMitekCaptureConfiguration = {
            mode: MitekConfiguration.current.captureMode,
            documentType: MitekProps.mitekDocumentType,
            mitekSDKPath: window.location.origin + '/etc.clientlibs/cob-corp-acquisition/clientlibs/clientlib-mitek/resources',
            options: {
                qualityPercent: MitekProps.mitekQualityLevel,
                guidePaddingLevel: MitekProps.mitekGuidePaddingLevel,
                license: MitekConfiguration.current.licenseKey || 'eyJzaWduYXR1cmUiOiJTdGFkNDY3Kzl6MFQwaVVjam9KZXg4Y21JWEZPQXlwVDdMRXlCVjhKMXpDeXdFY2h3VmNtZ1A5L1EvMWQzQlFpNDRCUnAzZlE4R0xZQ2VyRlZXMHhzY1VibThWWU0vOVYxcFgzb01qL0l4SmxxU1ZUT2M4RVpMQkpTemZpYk05RzFmeWNxQ1BTTy9va2hrU1RzUkJkemZOUGE2Q01ZWllwU3N3UDcxcHlKTk9Fb29qZ1FIMjNtRW1XQ1MyblhOVDI5RUF6RUl3Ynk1dTdxUE9sd29vWllENDNHR1hrTzFLb044N08xVld1NlI5czV4MU9yR3daR3FHTk1QenJlK0t6dnJVdjBMV1lxc3l0NkNZSmZVSWc0V2J0UDQ3Vkt3MTIxbElza0xXdmQreHFDc0gwNHE5aXhMUlRzb1Y0UnBwOGNOSlloZllKZEIyUnF0a0ZRT1ZXQkE9PSIsInNpZ25lZCI6eyJleHBpcnkiOiIyMDI3LTAzLTMwIiwiZmVhdHVyZXMiOlsiYmFyY29kZSIsImlkIiwiZmFjZSJdLCJhcHBsaWNhdGlvbklkZW50aWZpZXIiOnsid2ViIjpbIiouY3JlZGl0b25lLmNvbSIsImNyZWRpdG9uZS5jb20iLCIqLmNyZWRpdG9uZWJhbmsuY29tIiwiY3JlZGl0b25lYmFuay5jb20iXX0sImdwbyI6IjE2ZCIsIm9yZ2FuaXphdGlvbiI6Ik1pdGVrIFN5c3RlbXMgSW5jLiIsInZlcnNpb24iOiIxLjAifSwiYmFyY29kZVdlYkxpY2Vuc2UiOiIyV2xmRTR1RFRNbWJ0anlORGwrUERPTS9lYkw4aEFxUDJvMFR0eUR4bmt0N1ZDbmY5Q2s4SjdpWVNvM29lNWx2NC9tU0x4YzI5V21vMzZBdXQvTmMvaVlCUm1GOEZsQzBsNmZmVXhWQWliMVh4Y3B0L2gzKzhvNnJwbGJXQmtIekpFTE8zc1FTVTgzc2dpa1plb1ZGRTh2OVgzT1AvaEpoeVZZaGVUaENFR1JVY3lsU1k1WUM4YzFUTTB0eWpWMHZGMVU2V1QrcHFRUGUzOTkySHhEdUl1Y1hhRnVxT2xjT3d2a1p1UWpaS2l1OUxLVHFGSUFQTEJOdVd6blhnNmVoRW5PZDJJZHNsRFVjSmpPb3p1YldmTEt5NlRKVkI5cGwyZHRBKzVENG91S1lRWFBOOUErRG5VcDgzWkhPMDllM29iQTUxYXFmNjQzUjRpZm9DU1pwWUxray90SkZxNFBnb3hNVFF3elZDSzJFQ1J4SjNhSjczZEJ1R01Qa2RWM2gzVGMwSWdXUnN6eXBIcVpvOElVMnpqZ2JzRnRWeHprTDRCVkZyZi94QzZIN0RrQkRFNGhiRFdqWTZDeVVLbjBpY3FWeWVNU2l2N2s2Ym9GaTZZenRpTUZMMHdTL1dQSDFmdjJRZDZqZElpOURkTnh3RG1acmlTaDZUN1IrTVRmViJ9' as const,
            },
        };

        const mitek = (window as any).mitekScienceSDK;
        mitek.on(
            'FRAME_CAPTURE_RESULT',
            MitekHandleCaptureCallback(callback),
        );
        mitek.on(
            'FRAME_PROCESSING_FEEDBACK',
            MitekHandleFeedbackCallback,
        );

        mitek.on(
            'SDK_ERROR',
            ErrorHandlingHookGenerator({
                ...MitekProps,
                captureConfiguration,
                resultCallback: callback,
                pushDownContext: pushdownContext,
            }),
        );

        if (MitekProps.mitekSessionTimeout && MitekProps.mitekSessionTimeout > 0) {
            MitekSessionTimeoutHandle.current = setTimeout(() => {
                mitek.cmd('SDK_STOP');
            }, MitekProps.mitekSessionTimeout * 1000);
        }

        mitek.cmd(
            'CAPTURE_AND_PROCESS_FRAME',
            captureConfiguration,
        );
    };

    const clearResults = () => {
        for (const doc of DocumentTypes) {
            MitekResults.current[doc] = undefined;
        }
    };

    return (
        <MitekContext.Provider
            value={{
                MitekResults,
                MitekProps,
                MitekConfiguration,
                initMitek,
                startCapture: MitekCapture,
                retrieveResultsToSubmit,
                retrieveResultsToSubmitModern,
                clearResults,
                setMitekProps,
            }}
        >
            {children}
        </MitekContext.Provider>
    );
};
